import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { communicationApi, type CommunicationDashboard } from '../../api/communication';

const NAV = [
  { to: '/communication/campaigns', label: 'Campaign Manager' },
  { to: '/communication/templates', label: 'Template Builder' },
  { to: '/communication/notifications', label: 'Notification Center' },
  { to: '/communication/providers', label: 'Provider Manager' },
  { to: '/communication/delivery', label: 'Delivery Monitor' },
  { to: '/communication/analytics', label: 'Communication Analytics' },
];

export function CommunicationHubPage() {
  const [dashboard, setDashboard] = useState<CommunicationDashboard | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    void communicationApi.dashboard().then(setDashboard).catch((e) => setError(String(e)));
  }, []);

  const stats = dashboard
    ? [
        { label: 'Messages Sent (24h)', value: dashboard.messages_sent_24h },
        { label: 'Delivered (24h)', value: dashboard.messages_delivered_24h },
        { label: 'Delivery Rate', value: `${dashboard.delivery_rate.toFixed(1)}%` },
        { label: 'Active Campaigns', value: dashboard.active_campaigns },
        { label: 'Active Templates', value: dashboard.active_templates },
        { label: 'Unread Notifications', value: dashboard.unread_notifications },
        { label: 'Active Providers', value: dashboard.active_providers },
        { label: 'Open Rate', value: `${dashboard.open_rate}%` },
      ]
    : [];

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold">Communication Platform</h1>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
        Enterprise omnichannel communication, notification, messaging and campaign engine.
      </p>
      <nav className="mt-4 flex flex-wrap gap-3 text-sm">
        {NAV.map((l) => (
          <Link key={l.to} to={l.to} className="text-slate-700 hover:underline dark:text-slate-300">{l.label}</Link>
        ))}
      </nav>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl border bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
            <p className="text-sm text-slate-500">{s.label}</p>
            <p className="mt-1 text-2xl font-semibold">{String(s.value ?? 0)}</p>
          </div>
        ))}
      </div>
      {dashboard?.channel_breakdown && Object.keys(dashboard.channel_breakdown).length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium">Channel Breakdown</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {Object.entries(dashboard.channel_breakdown).map(([ch, count]) => (
              <div key={ch} className="rounded-lg border p-3 text-sm dark:border-slate-700">
                <span className="font-medium capitalize">{ch.replace('_', ' ')}</span>
                <span className="ml-2 text-slate-500">{count} messages</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {dashboard?.recent_activity && dashboard.recent_activity.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-medium">Recent Activity</h2>
          <ul className="mt-3 space-y-2">
            {dashboard.recent_activity.map((a) => (
              <li key={a.id} className="rounded-lg border p-3 text-sm dark:border-slate-700">
                <span className="font-medium">{a.action}</span> — {a.message}
                <span className="ml-2 text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
